// Shipping page. Tabs: Ready (in_production) vs Shipped (shipped+delivered).
// Inline tracking entry + "Mark shipped" / "Mark delivered" actions.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'

interface ShipRow {
  id: string; number: number; status: string; totalCents: number
  trackingNumber: string | null; trackingCarrier: string | null
  shippedAt: string | null; createdAt: string
  customerEmail: string | null
  shippingName: string | null; shippingCity: string | null; shippingCountry: string | null
}

export default function Shipping() {
  const { getAccessToken } = useAuth()
  const [state, setState] = useState<'ready' | 'shipped'>('ready')
  const [items, setItems] = useState<ShipRow[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, { number: string; carrier: string }>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ items: ShipRow[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/shipping?state=${state}`)
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }, [state, getAccessToken])
  useEffect(() => { void load() }, [load])

  async function ship(id: string) {
    const d = drafts[id]; if (!d?.number || !d?.carrier) { alert('Carrier + tracking number required'); return }
    setBusy(id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/shipping/${id}/ship`, {
        method: 'POST', body: JSON.stringify({ trackingNumber: d.number, trackingCarrier: d.carrier }),
      })
      setDrafts((x) => { const c = { ...x }; delete c[id]; return c })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(null) }
  }
  async function deliver(id: string) {
    // Confirm before flipping — once an order is marked delivered the
    // queue card disappears from this page, and there's no inline "undo"
    // for a mis-click (admin would have to go to /admin/orders/:id and
    // reverse the status manually).
    if (!confirm('Mark this order as delivered? It will leave the Shipping queue.')) return
    setBusy(id)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/shipping/${id}/deliver`, { method: 'POST' })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setBusy(null) }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="eyebrow">Production</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Shipping</h1>
      </div>

      <div className="flex gap-1.5 mb-5 border-b border-border pb-1">
        {(['ready','shipped'] as const).map((s) => (
          <button key={s} onClick={() => setState(s)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
              (state === s ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
            {s === 'ready' ? 'Ready to ship' : 'Shipped / delivered'}
          </button>
        ))}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      {items.length === 0 ? (
        <div className="card text-center text-ink-3 text-sm">{state === 'ready' ? 'Nothing ready to ship yet.' : 'No shipped orders.'}</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const d = drafts[r.id] ?? { number: '', carrier: '' }
            return (
              <div key={r.id} className="card">
                <div className="flex items-center gap-3 flex-wrap">
                  <Link to={`/admin/orders/${r.id}`} className="font-mono text-xs text-ink-2 hover:text-gold-dark">#{r.number}</Link>
                  <StatusBadge status={r.status} />
                  <div className="ml-auto font-semibold text-ink">${(r.totalCents / 100).toFixed(2)}</div>
                </div>
                <div className="text-ink mt-1 truncate">{r.shippingName} · {r.shippingCity}, {r.shippingCountry}</div>
                <div className="text-xs text-ink-3 truncate">{r.customerEmail}</div>
                {state === 'ready' ? (
                  <div className="mt-3 flex gap-2 items-end flex-wrap">
                    <div className="flex-1 min-w-[160px]">
                      <div className="eyebrow">Carrier</div>
                      <input value={d.carrier} onChange={(e) => setDrafts((x) => ({ ...x, [r.id]: { ...d, carrier: e.target.value } }))}
                        placeholder="Aus Post / DHL / …" className="input" />
                    </div>
                    <div className="flex-[2] min-w-[200px]">
                      <div className="eyebrow">Tracking number</div>
                      <input value={d.number} onChange={(e) => setDrafts((x) => ({ ...x, [r.id]: { ...d, number: e.target.value } }))}
                        placeholder="Tracking ID" className="input" />
                    </div>
                    <button disabled={busy === r.id} onClick={() => ship(r.id)} className="btn-gold">{busy === r.id ? '…' : 'Mark shipped'}</button>
                  </div>
                ) : (
                  <div className="mt-3 flex gap-3 items-center text-xs text-ink-2">
                    <span className="font-semibold text-ink">{r.trackingCarrier}</span>
                    <span className="font-mono">{r.trackingNumber}</span>
                    <span className="text-ink-3">Shipped {r.shippedAt ? new Date(r.shippedAt).toLocaleString() : '—'}</span>
                    {r.status === 'shipped' && (
                      <button disabled={busy === r.id} onClick={() => deliver(r.id)} className="ml-auto px-3 py-1 text-xs uppercase tracking-wide font-semibold rounded bg-teal-100 text-teal-800 hover:bg-teal-200 transition-colors">Mark delivered</button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
